import { useEffect } from 'react';
import { useQueryClient } from 'shared-ui';
import { toast } from 'react-toastify';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { editMultiCards } from 'api/cards/cards';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { useSelectedCard } from 'hooks/useSelectedCard/useSelectedCard';
import { useMutation } from 'hooks/useMutation/useMutation';
import { useModalState } from 'hooks/useModalState/useModalState';

import { BlockMultiCards } from './EditMultiCards';
import { EditMulitCardsFormBody, EditMultiCardsContainerProps } from './EditMultiCards.types';
import { useEditMultiCardsValidation } from './EditMultiCards.validation';

export const EditMultiCardsContainer = ({ checkedCards }: EditMultiCardsContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const { selectedCard, setSelectedCard } = useSelectedCard();
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();

  const { mutate: onBlockMultiCards, isLoading } = useMutation(editMultiCards, {
    onSuccess: (_, { body }) => {
      if (selectedCard && checkedCards.some(({ id }) => id === selectedCard?.id)) {
        setSelectedCard({ ...selectedCard, ...body });
      }

      queryClient.invalidateQueries([QueryKeyEnum.CARDS]);
      toast.dark(formatMessage({ id: AppMessages['cards.multiAction.edit.success'] }));
      onModalClose();
    },
  });

  const { schema } = useEditMultiCardsValidation();
  const formMethods = useForm<EditMulitCardsFormBody>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      nazwa: '',
      opis: '',
    },
  });

  useEffect(() => {
    formMethods.reset({ nazwa: '', opis: '' });
  }, [isModalOpen]);

  const onSubmit = (formBody: EditMulitCardsFormBody) => {
    onBlockMultiCards({ body: { ...formBody, kads: checkedCards.map(({ id }) => id) } });
  };

  return (
    <FormProvider {...formMethods}>
      <BlockMultiCards
        checkedCards={checkedCards}
        isModalOpen={isModalOpen}
        onModalClose={onModalClose}
        onModalOpen={onModalOpen}
        isLoading={isLoading}
        onSubmit={onSubmit}
      />
    </FormProvider>
  );
};
